import React, { useState, useCallback } from 'react';
import { useLoaderData, Link } from 'react-router-dom';
import { ContractorProfile, ContractorProfileUpdate, ContractorProfileCreate, Category, GalleryImage } from '../types/contractor';
import { Province } from '../api/locations';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Toast from '../components/Toast';
import ContractorProfileHeaderEditable from '../components/ContractorProfileHeaderEditable';
import AboutSectionEditable from '../components/AboutSectionEditable';
import ServicesCategoriesEditable from '../components/ServicesCategoriesEditable';
import GalleryOfWorksEditable from '../components/GalleryOfWorksEditable';
import BusinessInformationEditable from '../components/BusinessInformationEditable';
import OperationalDetailsEditable from '../components/OperationalDetailsEditable';
import { fetchContractorProfile, createContractorProfile, updateContractorProfile, fetchCategories } from '../api/contractorProfile';
import { fetchProvinces } from '../api/locations';

export async function contractorProfileLoader() {
  try {
    const [contractorProfile, categories, provinces] = await Promise.all([
      fetchContractorProfile(),
      fetchCategories(),
      fetchProvinces(),
    ]);
    return { contractorProfile, categories, provinces };
  } catch (error) {
    console.error('Error fetching contractor profile:', error);
    return { contractorProfile: null, categories: [], provinces: [] };
  }
}

// Kept for React Router compatibility but we handle save manually
export async function contractorProfileUpdateAction() {
  return null;
}

interface ValidationErrors {
  contactPerson?: string;
  address?: string;
  province?: string;
  city?: string;
  postalCode?: string;
  yearsOfExperience?: string;
}

interface ChecklistItem {
  label: string;
  done: boolean;
}

interface ProfileCompletionChecklistProps {
  items: ChecklistItem[];
}

const ProfileCompletionChecklist: React.FC<ProfileCompletionChecklistProps> = ({ items }) => {
  const completedCount = items.filter(i => i.done).length;
  if (completedCount === items.length) return null;
  const percent = Math.round((completedCount / items.length) * 100);

  return (
    <div className="mb-6 bg-secondary-container/40 border border-secondary-container rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-label-bold text-on-surface">Complete your profile</h2>
        <span className="text-sm font-label-bold text-secondary">{completedCount}/{items.length}</span>
      </div>
      <div className="w-full bg-surface-container rounded-full h-2 mb-4">
        <div
          className="bg-secondary h-2 rounded-full transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <ul className="space-y-2">
        {items.map(item => (
          <li key={item.label} className="flex items-center gap-2 text-sm">
            <span
              className={`material-symbols-outlined text-[18px] flex-shrink-0 ${item.done ? 'text-[#2E7D32]' : 'text-outline-variant'}`}
              style={item.done ? { fontVariationSettings: "'FILL' 1" } : undefined}
            >
              {item.done ? 'check_circle' : 'radio_button_unchecked'}
            </span>
            <span className={item.done ? 'text-on-surface-variant line-through' : 'text-on-surface'}>
              {item.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ContractorProfilePage: React.FC = () => {
  const { contractorProfile, categories, provinces } = useLoaderData() as {
    contractorProfile: ContractorProfile | null;
    categories: Category[];
    provinces: Province[];
  };

  const isNew = contractorProfile === null;

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Profile fields
  const [profilePictureUrl, setProfilePictureUrl] = useState(contractorProfile?.profilePictureUrl ?? '');
  const [companyName, setCompanyName] = useState(contractorProfile?.companyName ?? '');
  const [aboutText, setAboutText] = useState(contractorProfile?.bio ?? '');
  const [contactPerson, setContactPerson] = useState(contractorProfile?.contactPerson ?? '');

  // Categories (by ID)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(() => {
    if (!contractorProfile?.categories || !categories) return [];
    return categories.filter(c => contractorProfile.categories.includes(c.name)).map(c => c.id);
  });

  // Service areas (pre-populated from serviceAreaDetails)
  const [serviceAreaTownIds, setServiceAreaTownIds] = useState<number[]>(
    contractorProfile?.serviceAreaDetails?.map(sa => sa.townId) ?? []
  );
  const [townNames, setTownNames] = useState<Record<number, string>>(
    Object.fromEntries(contractorProfile?.serviceAreaDetails?.map(sa => [sa.townId, sa.townName]) ?? [])
  );

  // Gallery
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>(contractorProfile?.galleryImages ?? []);

  // Business information
  const [contactEmail, setContactEmail] = useState(contractorProfile?.contactEmail ?? '');
  const [phone, setPhone] = useState(contractorProfile?.phoneNumber ?? '');
  const [address, setAddress] = useState(contractorProfile?.address ?? '');
  const [province, setProvince] = useState(contractorProfile?.province ?? '');
  const [city, setCity] = useState(contractorProfile?.city ?? '');
  const [postalCode, setPostalCode] = useState(contractorProfile?.postalCode ?? '');
  const [website, setWebsite] = useState(contractorProfile?.website ?? '');
  const [yearsInBusiness, setYearsInBusiness] = useState(contractorProfile?.yearsOfExperience ?? 0);

  const validate = (): ValidationErrors => {
    const errs: ValidationErrors = {};
    if (!contactPerson.trim()) errs.contactPerson = 'Contact person is required.';
    if (!address.trim()) errs.address = 'Street address is required.';
    if (!province) errs.province = 'Province is required.';
    if (!city) errs.city = 'City / town is required.';
    if (!postalCode.trim()) errs.postalCode = 'Postal code is required.';
    if (!yearsInBusiness && yearsInBusiness !== 0) errs.yearsOfExperience = 'Years of experience is required.';
    return errs;
  };

  const handleSaveChanges = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSaving(true);

    try {
      if (isNew) {
        const payload: ContractorProfileCreate = {
          companyName,
          contactPerson,
          contactEmail,
          phoneNumber: phone,
          address,
          city,
          province,
          postalCode,
          website: website.trim() || undefined,
          bio: aboutText,
          profilePictureUrl,
          yearsOfExperience: yearsInBusiness,
          categoryIds: selectedCategoryIds,
          serviceAreaTownIds,
          galleryImages,
        };
        await createContractorProfile(payload);
      } else {
        const payload: ContractorProfileUpdate = {
          companyName,
          contactPerson,
          contactEmail,
          phoneNumber: phone,
          address,
          city,
          province,
          postalCode,
          website: website.trim() || undefined,
          bio: aboutText,
          profilePictureUrl,
          yearsOfExperience: yearsInBusiness,
          categoryIds: selectedCategoryIds,
          serviceAreaTownIds,
          galleryImages,
        };
        await updateContractorProfile(payload);
      }
      setToast({ message: 'Profile saved.', type: 'success' });
    } catch {
      setToast({ message: 'Save failed. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const dismissToast = useCallback(() => setToast(null), []);

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased pb-32">
      <TopAppBar />

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}

      <main className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          {isNew ? 'Set Up Your Profile' : 'Edit Your Profile'}
        </h1>
        <ProfileCompletionChecklist
          items={[
            { label: 'Add your company name', done: !!companyName.trim() },
            { label: 'Choose at least one category', done: selectedCategoryIds.length > 0 },
            { label: 'Add at least one service area', done: serviceAreaTownIds.length > 0 },
            { label: 'Upload a profile picture', done: !!profilePictureUrl.trim() },
            { label: 'Write a bio', done: !!aboutText.trim() },
          ]}
        />
        <div className="bg-white p-8 rounded-lg shadow-md">
          <ContractorProfileHeaderEditable
            profilePictureUrl={profilePictureUrl}
            onProfilePictureUrlChange={setProfilePictureUrl}
            companyName={companyName}
            onCompanyNameChange={setCompanyName}
            isVerified={contractorProfile?.isVerified ?? false}
          />
          <AboutSectionEditable aboutText={aboutText} onAboutTextChange={setAboutText} />
          <ServicesCategoriesEditable
            categories={categories}
            selectedCategoryIds={selectedCategoryIds}
            onSelectedCategoryIdsChange={setSelectedCategoryIds}
            serviceAreaTownIds={serviceAreaTownIds}
            onServiceAreaTownIdsChange={setServiceAreaTownIds}
            provinces={provinces}
            townNames={townNames}
            onTownNamesUpdate={setTownNames}
          />
          <GalleryOfWorksEditable images={galleryImages} onChange={setGalleryImages} />
          <BusinessInformationEditable
            contactPerson={contactPerson}
            onContactPersonChange={setContactPerson}
            contactPersonError={errors.contactPerson}
            phone={phone}
            onPhoneChange={setPhone}
            email={contactEmail}
            onEmailChange={setContactEmail}
            address={address}
            onAddressChange={setAddress}
            addressError={errors.address}
            province={province}
            onProvinceChange={setProvince}
            provinceError={errors.province}
            city={city}
            onCityChange={setCity}
            cityError={errors.city}
            postalCode={postalCode}
            onPostalCodeChange={setPostalCode}
            postalCodeError={errors.postalCode}
            website={website}
            onWebsiteChange={setWebsite}
            provinces={provinces}
          />
          <OperationalDetailsEditable
            yearsInBusiness={yearsInBusiness}
            onYearsInBusinessChange={setYearsInBusiness}
          />
          {!isNew && (
            <div className="mt-6 bg-surface-container-low rounded-xl p-5 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-on-surface">Compliance &amp; Credentials</h2>
                <p className="text-sm text-outline mt-1">Manage your credentials and submitted documents.</p>
              </div>
              <Link
                to="/contractor-profile/credentials"
                className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                Manage <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
              </Link>
            </div>
          )}
          {!isNew && (
            <div className="mt-6 bg-surface-container-low rounded-xl p-5 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-on-surface">Resource Catalogue</h2>
                <p className="text-sm text-outline mt-1">Manage your standard rates for faster quoting.</p>
              </div>
              <Link
                to="/resource-catalogue"
                className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                Manage <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Sticky save bar */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 px-4 py-3 flex items-center justify-end gap-4">
        {Object.keys(errors).length > 0 && (
          <p className="text-red-500 text-sm">Please fill in the required fields.</p>
        )}
        <button
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-2.5 px-8 rounded-lg text-base transition-colors"
          onClick={handleSaveChanges}
          disabled={saving}
        >
          {saving ? 'Saving...' : isNew ? 'Create Profile' : 'Save Profile'}
        </button>
      </div>

      <BottomNavBar />
    </div>
  );
};

export default ContractorProfilePage;
